import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from '@cloudsign/shared';
import type { BadgeMedia } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { useSite } from '../../lib/site';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, ConfirmButton, EmptyState, FilterBar, SelectField, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { BASE, PRINTER_MEDIA, PRINTER_MODELS, PrinterList, keys } from './api';

type Model = (typeof PRINTER_MODELS)[number];

interface Form {
  siteId: string;
  name: string;
  model: Model;
  host: string;
  port: string;
  media: BadgeMedia;
  status: 'active' | 'archived';
}

function PrinterForm({ printer, onDone }: { printer: Printer | null; onDone: () => void }) {
  const qc = useQueryClient();
  const { sites, siteId } = useSite();
  const [form, setForm] = useState<Form>({
    siteId: printer?.siteId ?? siteId ?? sites[0]?.id ?? '',
    name: printer?.name ?? '',
    model: printer?.model ?? 'brother_ql_820nwb',
    host: printer?.host ?? '',
    port: String(printer?.port ?? 9100),
    media: printer?.media ?? 'dk11202_62x100',
    status: printer?.status ?? 'active',
  });
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: () => {
      const body = { name: form.name, model: form.model, host: form.host, port: Number(form.port), media: form.media };
      return printer
        ? api.patch(`${BASE}/printers/${printer.id}`, { body: { ...body, status: form.status }, schema: Printer })
        : api.post(`${BASE}/printers`, { body: { ...body, siteId: form.siteId }, schema: Printer });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['devices'] });
      onDone();
    },
  });
  const errs = fieldErrors(save.error);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card title={printer ? t('devicesUi.printers.editTitle', { name: printer.name }) : t('devicesUi.printers.createTitle')} className="mb-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <ErrorLine error={save.error} />
        </div>
        <TextField label={t('devicesUi.printers.field.name')} required maxLength={60} value={form.name} onChange={(e) => set('name', e.target.value)} error={errs['name']} />
        {printer ? (
          <SelectField label={t('devicesUi.printers.field.status')} value={form.status} onChange={(e) => set('status', e.target.value as Form['status'])}>
            <option value="active">{t('status.active')}</option>
            <option value="archived">{t('status.archived')}</option>
          </SelectField>
        ) : (
          <SelectField label={t('devicesUi.printers.field.site')} value={form.siteId} onChange={(e) => set('siteId', e.target.value)} error={errs['siteId']}>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </SelectField>
        )}
        <SelectField label={t('devicesUi.printers.field.model')} value={form.model} onChange={(e) => set('model', e.target.value as Model)}>
          {PRINTER_MODELS.map((m) => (
            <option key={m} value={m}>
              {t(`devicesUi.model.${m}`)}
            </option>
          ))}
        </SelectField>
        <SelectField label={t('devicesUi.printers.field.media')} value={form.media} onChange={(e) => set('media', e.target.value as BadgeMedia)} error={errs['media']}>
          {PRINTER_MEDIA.map((m) => (
            <option key={m} value={m}>
              {t(`devicesUi.media.${m}`)}
            </option>
          ))}
        </SelectField>
        <TextField
          label={t('devicesUi.printers.field.host')}
          hint={t('devicesUi.printers.field.hostHint')}
          required
          maxLength={253}
          value={form.host}
          onChange={(e) => set('host', e.target.value)}
          error={errs['host']}
        />
        <TextField
          label={t('devicesUi.printers.field.port')}
          type="number"
          min={1}
          max={65535}
          required
          value={form.port}
          onChange={(e) => set('port', e.target.value)}
          error={errs['port']}
        />
        <div className="flex items-end gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={save.isPending || form.name.trim() === '' || form.host.trim() === ''}>
            {printer ? t('common.save') : t('devicesUi.printers.submit.create')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

export function PrintersTab() {
  const { sites } = useSite();
  const canManage = useCan('sites.manage_devices');
  const qc = useQueryClient();
  const toast = useToast();
  const [siteFilter, setSiteFilter] = useState('');
  const [editing, setEditing] = useState<Printer | 'new' | null>(null);
  const q = useQuery({
    queryKey: keys.printers(siteFilter || 'all'),
    queryFn: () => api.get(`${BASE}/printers`, { query: { siteId: siteFilter || undefined }, schema: PrinterList }),
  });
  const remove = useMutation({
    mutationFn: (p: Printer) => api.delete(`${BASE}/printers/${p.id}`),
    onSuccess: async () => {
      toast(t('devicesUi.printers.deleted'));
      await qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? '';
  const newButton = canManage ? (
    <Button variant="primary" onClick={() => setEditing('new')} disabled={editing !== null}>
      {t('devicesUi.printers.new')}
    </Button>
  ) : null;

  return (
    <>
      {newButton ? <div className="mb-3 flex justify-end">{newButton}</div> : null}
      {editing ? (
        <PrinterForm key={editing === 'new' ? 'new' : editing.id} printer={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      ) : null}
      <FilterBar columns="sm:grid-cols-2">
        <SelectField label={t('devicesUi.filter.site')} value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)}>
          <option value="">{t('devicesUi.filter.allSites')}</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </SelectField>
      </FilterBar>
      <ErrorLine error={remove.error} />
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <TableWrap caption={t('devicesUi.tab.printers')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('devicesUi.printers.col.name')}</Th>
              <Th>{t('devicesUi.printers.col.site')}</Th>
              <Th>{t('devicesUi.printers.col.model')}</Th>
              <Th>{t('devicesUi.printers.col.address')}</Th>
              <Th>{t('devicesUi.printers.col.media')}</Th>
              <Th>{t('devicesUi.printers.col.state')}</Th>
              {canManage ? <Th>{t('devicesUi.printers.col.actions')}</Th> : null}
            </tr>
          </thead>
          <tbody>
            {q.data.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState message={t('devicesUi.printers.empty')} action={newButton} />
                </td>
              </tr>
            ) : null}
            {q.data.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 align-top hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">
                  {p.name}
                  {p.status === 'archived' ? (
                    <div className="mt-1">
                      <Badge>{t('devicesUi.printers.archived')}</Badge>
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3">{siteName(p.siteId)}</td>
                <td className="px-4 py-3">{t(`devicesUi.model.${p.model}`)}</td>
                <td className="px-4 py-3">
                  {p.host}:{p.port}
                </td>
                <td className="px-4 py-3">{t(`devicesUi.media.${p.media}`)}</td>
                <td className="px-4 py-3">
                  {p.lastStatus ? (
                    <>
                      <Badge tone={p.lastStatus.state === 'ready' || p.lastStatus.state === 'busy' ? 'good' : 'bad'}>
                        {t(`devicesUi.printerState.${p.lastStatus.state}`)}
                      </Badge>
                      {p.lastStatus.labelsLeft !== null ? (
                        <div className="mt-1 text-xs text-slate-600">{t('devicesUi.printers.labelsLeft', { count: p.lastStatus.labelsLeft })}</div>
                      ) : null}
                    </>
                  ) : (
                    <Badge>{t('devicesUi.printerState.unknown')}</Badge>
                  )}
                </td>
                {canManage ? (
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button onClick={() => setEditing(p)} aria-label={`${t('common.edit')} ${p.name}`}>
                        {t('common.edit')}
                      </Button>
                      <ConfirmButton label={`${t('devicesUi.printers.delete')} ${p.name}`} pending={remove.isPending} onConfirm={() => remove.mutate(p)} />
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
    </>
  );
}
